<template>
  <v-row>
    <v-col class="py-0" cols="12">
      <v-btn
        color="warning"
        fixed
        bottom
        right
        fab
        title="Apply Changes"
        @click="applyChanges()"
        v-if="needsApply"
      >
        <v-icon large>mdi-restart</v-icon>
      </v-btn>

      <v-form
        ref="form"
        v-model="valid"
        @submit.prevent="saveElaborator"
        lazy-validation
      >
        <v-card :loading="saving">
          <v-card-title>
            <v-icon v-if="elaborator.__hasChanges" color="primary"
              >mdi-content-save-alert-outline</v-icon
            >
            <v-icon v-if="!elaborator.__hasChanges"
              >mdi-plus-box-multiple</v-icon
            >
            <span class="pl-2 headline">
              Elaborator
              <span class="pl-2 subtitle-1">
                <TimeoutMessage v-model="saving">
                  <span class="primary--text">Saving...</span>
                </TimeoutMessage>
              </span>
            </span>
          </v-card-title>
          <v-card-text>
            <v-alert
              border="left"
              color="warning"
              type="warning"
              dense
              text
              colored-border
              v-if="needsApply"
            >
              <v-row align="center">
                <v-col class="grow">
                  Your
                  <b>elaborator</b> has changes that need to be applied...
                </v-col>
                <v-col class="shrink">
                  <v-btn color="warning" @click="applyChanges()">
                    <v-icon>mdi-restart</v-icon>Apply
                  </v-btn>
                </v-col>
              </v-row>
            </v-alert>
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="Name*"
                    hint="A short name used to identify this action"
                    v-model="elaborator.name"
                    @blur="titleCase('name')"
                    :error-messages="error.errors && error.errors.id"
                    :readonly="
                      elaborator.properties.forCustomRegistrationPrompts
                    "
                    validate-on-blur
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6"></v-col>
                <v-col
                  cols="12"
                  v-if="!elaborator.properties.forCustomRegistrationPrompts"
                >
                  <v-text-field
                    label="Description*"
                    v-model="elaborator.description"
                    validate-on-blur
                    required
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row v-if="elaborator.properties.forCustomRegistrationPrompts">
                <v-col cols="12">
                  <p>
                    These custom registration prompts will be asked after the
                    selected registration prompts. It is advised that any custom
                    prompts be limited to as few as possible so that the
                    registration process is uncomplicated and fast.
                  </p>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <v-subheader class="title">Messages</v-subheader>
                  <v-divider></v-divider>
                </v-col>
                <v-col cols="12">
                  <v-expansion-panels
                    v-if="!elaborator.properties.forCustomRegistrationPrompts"
                  >
                    <v-expansion-panel>
                      <v-expansion-panel-header
                        :color="
                          $vuetify.theme.dark
                            ? 'grey darken-2'
                            : 'grey lighten-1'
                        "
                        >Introductory message</v-expansion-panel-header
                      >
                      <v-expansion-panel-content class="pt-4">
                        <p>
                          This message introduces the purpose of the elaborator
                          to the user and is the prompt they'll see before
                          continuing or cancelling the elaborator.
                        </p>
                        <Editor
                          v-model="elaborator.properties.introMessage"
                          label
                          hint
                          :id="elaborator.id"
                          :error="error"
                          :placeholders="[placeholders[0]]"
                        ></Editor>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel>
                      <v-expansion-panel-header
                        :color="
                          $vuetify.theme.dark
                            ? 'grey darken-2'
                            : 'grey lighten-1'
                        "
                        >Complete message</v-expansion-panel-header
                      >
                      <v-expansion-panel-content class="pt-4">
                        <p>
                          This message shows upon completion of the elaborator.
                          Explain what will happen to the information next or
                          simply thank the user.
                        </p>
                        <Editor
                          v-model="elaborator.properties.completeMessage"
                          label
                          hint
                          :id="elaborator.id"
                          :error="error"
                          :placeholders="[placeholders[0]]"
                        ></Editor>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
              </v-row>

              <v-row>
                <v-col class="mb-3" cols="12">
                  <v-subheader class="title">Prompts</v-subheader>
                  <v-divider></v-divider>
                  <v-alert
                    class="mt-2 mb-0"
                    type="error"
                    dense
                    text
                    v-if="!valid && !openPrompt"
                  >
                    <v-row align="center">
                      <v-col class="grow">
                        There are errors in one or more scenes...
                      </v-col>
                    </v-row>
                  </v-alert>
                </v-col>
                <v-btn
                  class="mx-3 mt-3"
                  fab
                  dark
                  small
                  color="primary"
                  :absolute="true"
                  :right="true"
                  @click="addScene()"
                >
                  <v-icon dark>mdi-plus</v-icon>
                </v-btn>
                <v-expansion-panels
                  :dark="$vuetify.theme.dark"
                  popout
                  v-model="openPrompt"
                >
                  <draggable
                    v-model="elaborator.scenes"
                    @start="drag = true"
                    @end="drag = false"
                    :move="onSceneReorder"
                    class="draggable-panel"
                  >
                    <v-expansion-panel
                      v-for="(scene, index) in elaborator.scenes"
                      :key="index"
                    >
                      <v-expansion-panel-header
                        :color="
                          $vuetify.theme.dark
                            ? 'grey darken-3'
                            : 'grey lighten-2'
                        "
                        disable-icon-rotate
                      >
                        <span
                          ><v-icon class="drag-handle">mdi-drag</v-icon>
                          {{ index + 1 }}. {{ scene.prompt.message }}</span
                        >
                        <template v-slot:actions>
                          <v-chip
                            class="mx-2 font-weight-bold"
                            color
                            v-if="scene.id"
                            >{{ scene.id }}</v-chip
                          >
                          <v-btn
                            class="mx-1"
                            color="warning"
                            title="Conditional"
                            fab
                            dark
                            x-small
                            v-if="scene.conditions && scene.conditions.length"
                          >
                            <span class="title">C</span>
                          </v-btn>
                          <v-btn
                            class="mx-1"
                            color="info"
                            :title="scene.type | title_case"
                            fab
                            dark
                            x-small
                            v-if="scene.type"
                          >
                            <!--<span class="title">{{scene.type.charAt(0)}}</span>-->
                            <v-icon>{{ sceneIcon(scene.type) }}</v-icon>
                          </v-btn>
                          <v-btn
                            :disabled="index === 0"
                            @click.stop="sceneUp(index)"
                            icon
                          >
                            <v-icon>mdi-arrow-up</v-icon>
                          </v-btn>
                          <v-btn
                            :disabled="index + 1 >= elaborator.scenes.length"
                            @click.stop="sceneDown(index)"
                            icon
                          >
                            <v-icon>mdi-arrow-down</v-icon>
                          </v-btn>
                        </template>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <Scene
                          :scene.sync="scene"
                          :scenes.sync="elaborator.scenes"
                          :error="error"
                          :condition-scenes="conditionScenes"
                          :remove="removeScene"
                          @changed="debouncedSave(true, elaborator)"
                        ></Scene>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </draggable>
                </v-expansion-panels>
              </v-row>

              <v-row
                class="mt-3"
                v-if="!elaborator.properties.forCustomRegistrationPrompts"
              >
                <v-col cols="12">
                  <v-subheader class="title">Elaborated Message</v-subheader>
                  <v-divider></v-divider>
                  <p class="mt-4 mb-0">
                    The elaborated message can be constructed using the user's
                    prompt inputs. This is the message that will be used in the
                    other chosen actions.
                  </p>
                </v-col>
                <v-col cols="12">
                  <Editor
                    v-model="elaborator.properties.elaboratedMessageTemplate"
                    :id="elaborator.id"
                    :placeholders="placeholders"
                    :error="error"
                  ></Editor>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              class="ma-2"
              outlined
              type="submit"
              :loading="saving"
              v-if="!elaborator.id"
              >Create Elaborator</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-form>
    </v-col>
  </v-row>
</template>

<script>
import draggable from "vuedraggable";
import { mapGetters, mapActions } from "vuex";
import Scene from "./Scene";
import Editor from "../widget/Editor";
import TimeoutMessage from "../widget/TimeoutMessage";

export default {
  data: () => ({
    openPrompt: undefined,
    debouncedSave: null,
    saving: false,
    showSaving: false,
    valid: true,
    needsApply: false,
    error: {},
    conditionScenes: [],
    sceneIndexMap: {},
    sceneTemplate: {
      id: "",
      type: "",
      prompt: {
        message: "",
      },
      validation: {
        expect: "",
      },
      conditions: [],
      options: {
        required: true,
      },
    },
  }),
  created() {},
  mounted() {
    this.debouncedSave = this.$debounce(this.saveElaborator, 3000);
  },
  computed: {
    ...mapGetters("apiAuth", ["user"]),
    ...mapGetters("content", {
      memberDefaultPlaceholders: "memberPlaceholders",
    }),
    memberPlaceholders() {
      if (
        this.customRegitrationPromptElaborator &&
        this.customRegitrationPromptElaborator.scenes
      ) {
        this.customRegitrationPromptElaborator.scenes.forEach((scene) => {
          this.memberDefaultPlaceholders.push({
            text: this.$options.filters.title_case(scene.id),
            placeholder: `{{it.member.custom.${scene.id}}}`,
            type: 'Custom'
          });
        });
      }
      return this.memberDefaultPlaceholders;
    },
    placeholders() {
      let promptPlaceholders = [];

      this.elaborator.scenes.forEach((scene) => {
        if (
          scene.type === "text" ||
          scene.type === "select" ||
          scene.type === "address"||
          scene.type === "license_plate"
        ) {
          promptPlaceholders.push({
            text: scene.id,
            placeholder: `{{it.${scene.id}}}`,
            type: "Value",
          });
        }
        if (scene.type === "location" || scene.type === "coordinates") {
          promptPlaceholders.push({
            text: scene.id,
            placeholder: `{{@location(it.${scene.id})/}}`,
            type: "Location",
          });
        }
        if (scene.type === "binary") {
          promptPlaceholders.push({
            text: `${scene.id} = Yes`,
            placeholder: `{{ @if (it.${scene.id} === true) }}{{ /if}}`,
            type: "Condition",
          });
          promptPlaceholders.push({
            text: `${scene.id} = No`,
            placeholder: `{{ @if (it.${scene.id} === false) }}{{ /if}}`,
            type: "Condition",
          });
        }
        if (scene.type === "select" && scene.prompt.options) {
          scene.prompt.options.forEach((option) => {
            promptPlaceholders.push({
              text: `${scene.id} = ${option}`,
              placeholder: `{{ @if (it.${scene.id} === "${option}") }}{{ /if}}`,
              type: "Condition",
            });
          });
        }
      });

      return [
        {
          label: "Member",
          placeholders: this.memberPlaceholders,
        },
        {
          label: "Prompts",
          placeholders: promptPlaceholders,
        },
      ];
    },
    bot() {
      return this.user &&
        this.user.account &&
        this.user.account.bots &&
        this.user.account.bots.length
        ? this.user.account.bots[0]
        : null;
    },
  },
  methods: {
    ...mapActions("app", ["showMessage"]),
    ...mapActions("heroku", { reboot: "remove" }),
    validate() {
      return this.$refs.form.validate();
    },
    resetValidation() {
      this.$refs.form.resetValidation();
      this.error = {};
    },
    sceneIcon(type) {
      let sceneIcons = {
        text: "mdi-format-text",
        select: "mdi-form-select",
        binary: "mdi-power-cycle",
        location: "mdi-map-marker",
        address: "mdi-home-map-marker",
        license_plate: "mdi-car",
        reference: "mdi-magnify",
        photo: "mdi-camera",
        file: "mdi-file",
      };
      return sceneIcons[type] || "mdi-question";
    },
    titleCase(field) {
      this.elaborator[field] = this.$options.filters.title_case(
        this.elaborator[field]
      );
    },
    onSceneReorder() {
      this.sceneSetup();
    },
    sceneUp(index) {
      const newSceneOrder = [...this.elaborator.scenes];
      newSceneOrder[index] = this.elaborator.scenes[index - 1];
      newSceneOrder[index - 1] = this.elaborator.scenes[index];
      this.elaborator.scenes = newSceneOrder;
      this.sceneSetup();
    },
    sceneDown(index) {
      const newSceneOrder = [...this.elaborator.scenes];
      newSceneOrder[index] = this.elaborator.scenes[index + 1];
      newSceneOrder[index + 1] = this.elaborator.scenes[index];
      this.elaborator.scenes = newSceneOrder;
      this.sceneSetup();
    },
    addScene() {
      let newScene = JSON.parse(JSON.stringify(this.sceneTemplate));
      this.elaborator.scenes.push(newScene);
      this.openPrompt = this.elaborator.scenes.length - 1;
      this.sceneSetup();
    },
    removeScene(scene) {
      this.elaborator.scenes.splice(scene.index, 1);
      this.sceneSetup();
    },
    sceneSetup() {
      this.getConditionScenes();
      this.reIndexScenes();
      this.removeInvalidConditions();
    },
    removeInvalidConditions() {
      this.elaborator.scenes.forEach((scene, sceneIdx) => {
        if (scene.conditions && scene.conditions.length) {
          var conditionsCount = scene.conditions.length;
          while (conditionsCount--) {
            if (
              this.sceneIndexMap[scene.conditions[conditionsCount].id] >
              sceneIdx
            ) {
              console.log("Removed invalid condition:", scene.conditions);
              scene.conditions.splice(conditionsCount, 1);
            }
          }
        }
      });
    },
    reIndexScenes() {
      this.elaborator.scenes.forEach((scene, index) => {
        this.sceneIndexMap[scene.id] = index;
        scene.index = index;
      });
    },
    getConditionScenes() {
      this.conditionScenes = [];

      this.elaborator.scenes.forEach((scene) => {
        if (["text", "select", "binary"].includes(scene.type)) {
          if (
            scene.type !== "text" ||
            (scene.type === "text" &&
              scene.validation &&
              scene.validation.expect === "number")
          ) {
            this.conditionScenes.push(scene);
          }
        }
      });
    },
    async saveElaborator(auto, elaborator) {
      if (!this.validate()) {
        if (!auto)
          this.showMessage({ text: "You missed something!", color: "error" });
        return;
      } else if (!this.elaborator.scenes.length) {
        if (!auto)
          this.showMessage({
            text: "Please add at least one prompt!",
            color: "warning",
          });
        return;
      }

      this.saving = true;
      this.showSaving = true;
      try {
        if (elaborator) {
          await elaborator.save();
          elaborator.__hasChanges = false;
        } else await this.elaborator.save();
        //this.showMessage({ text: "Saved!", color: "", position: "top" });
      } catch (error) {
        this.error = error;
        this.showMessage({
          text: error.message || "Error occured!",
          color: "red",
        });
      }
      this.saving = false;
      this.needsApply = true;
    },
    async applyChanges() {
      let isValid = this.validate();

      if (isValid && this.bot) {
        this.showMessage({
          text: "Changes are being applied...",
          timeout: 12000,
          position: "top",
        });
        await this.reboot(this.bot.app_id);
        this.needsApply = false;
      } else if (!isValid) {
        this.showMessage({
          text: "You have one or more invalid prompts...",
          color: "error",
          position: "top",
        });
      }
    },
  },
  watch: {
    elaborator: {
      handler(now, before) {
        if (now.id && now.id !== before.id) {
          if (before.id && before.__hasChanges) {
            this.debouncedSave(true, before);
          }
        } else if (now.id) {
          now.__hasChanges = true;
          this.debouncedSave(true, now);
        }

        if (!this.valid) {
          this.resetValidation();
        }
      },
      deep: true,
    },
    "elaborator.properties.forCustomRegistrationPrompts": {
      handler(custom) {
        if (custom && !this.elaborator.name) {
          this.elaborator.name = "Custom Registration Prompts";
        }
      },
    },
    openPrompt() {
      this.sceneSetup();
    },
  },
  beforeDestroy() {
    if (this.needsApply) this.applyChanges();
  },
  components: {
    Scene,
    Editor,
    TimeoutMessage,
    draggable,
  },
  props: {
    elaborator: {
      type: Object,
      default: () => {
        return {};
      },
    },
    groups: {
      type: Array,
      default: () => {
        return [];
      },
    },
    customRegitrationPromptElaborator: {
      type: Object,
      default: () => {
        return {};
      },
    },
    canBeCustomRegitrationPromptElaborator: {
      type: Boolean,
      default: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.apply-btn {
  z-index: 10;
  margin-top: 50px;
}

.draggable-panel {
  width: 100%;
}

.drag-handle {
  cursor: grab;
}
</style>